import {Pagination} from '@/components';
import {TableSkeleton} from '@/components/Table/TableSkeleton';

import NameResolutionFilters from './NameResolutionFilters';
import NameResolutionTable from './NameResolutionTable';
import {PAGE_SIZE} from './types';
import useNameResolutionList from './useNameResolutionList';

const resolveEmptyStateMessage = (needsCountry: boolean, hasError: boolean) => {
  if (needsCountry) {
    return 'Pick a country to search a mobile money number.';
  }
  if (hasError) {
    return "Couldn't load cached lookups. Try again.";
  }
  return 'No cached lookups match these filters.';
};

/**
 * The Cached lookups tab of Processors -> Resolvers. Account-name lookups are
 * cached and replayed, so after a resolver change or a wrong name the selected
 * resolver is never asked again for that account. Invalidating a row sends the
 * next lookup to the resolver, which caches the fresh result.
 */
const NameResolutionCache = () => {
  const {
    filters,
    setFilter,
    resolverOptions,
    needsCountry,
    list,
    total,
    filterKey,
    isLoading,
    error,
    onPageChange,
    invalidate,
  } = useNameResolutionList();

  const loadedContent = list.length ? (
    <NameResolutionTable items={list} onInvalidate={invalidate} />
  ) : (
    <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
      {resolveEmptyStateMessage(needsCountry, Boolean(error))}
    </p>
  );

  return (
    <>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Account-name lookups are cached and replayed instead of calling the
        resolver again. Invalidate one to send the next lookup for that account
        to the currency&rsquo;s current resolver.
      </p>
      <NameResolutionFilters
        filters={filters}
        resolverOptions={resolverOptions}
        setFilter={setFilter}
      />

      {!isLoading && !needsCountry ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {total.toLocaleString()}{' '}
          {total === 1 ? 'cached lookup' : 'cached lookups'}
        </p>
      ) : null}

      <section className="pt-3">
        {isLoading ? <TableSkeleton /> : loadedContent}
      </section>

      {total > PAGE_SIZE ? (
        <Pagination
          key={filterKey}
          limit={PAGE_SIZE}
          onPageChange={onPageChange}
          total={total}
        />
      ) : null}
    </>
  );
};

export default NameResolutionCache;
